import { baseQuery } from '../../baseQuery/baseQuery';
import { createApi } from '@reduxjs/toolkit/query/react';
import { getMarketBasedOnHostname } from '~/helpers/utils';
import {
  IOpenBankingAccount,
  IOpenBankingAccountBBAN,
  IOpenBankingAccountIBAN,
} from '~/interfaces/shared';
import { CountryCode } from '@qred/shared-enums';
import { TLoanApplicationDTO } from '~/types/DTO/loanApplication';
import { TPaymentPlanDTO } from '~/types/DTO/paymentPlan';
const market = getMarketBasedOnHostname();
type GetBankAccountsResultTypeDTO = {
  iban: string | null;
  bban: string | null;
  name: string | null;
  details: string | null;
}[];

type GetBankAccountsResultType = IOpenBankingAccount[];
interface GetBankAccountsQueryArg {
  clientId: number;
}

type GetIndicativeOfferQueryArg = { applicationUuid: string };

type GetPaymentPlanQueryArg = {
  applicationUuid: string;
  loanAmount: number;
  loanTerm: number;
};

export const onboardingApi = createApi({
  reducerPath: 'onboardingApi',
  baseQuery: baseQuery(
    `${import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL}/country/${market}`
  ),
  endpoints: (build) => ({
    getBankAccounts: build.query<
      GetBankAccountsResultType,
      GetBankAccountsQueryArg
    >({
      query: ({ clientId }) => ({
        url: `/bank-accounts/${clientId}`,
      }),
      transformResponse: (response: GetBankAccountsResultTypeDTO) => {
        if (market) {
          let accounts;
          switch (market) {
            case CountryCode.SE:
            case CountryCode.DK:
            case CountryCode.NO:
              accounts = response
                .filter((item) => item.bban)
                .map(
                  (item): IOpenBankingAccountBBAN => ({
                    ...item,
                    bban: item.bban!,
                    market,
                  })
                );
              break;
            case CountryCode.FI:
            case CountryCode.BE:
            case CountryCode.NL:
            case CountryCode.DE:
              accounts = response
                .filter((item) => item.iban)
                .map(
                  (item): IOpenBankingAccountIBAN => ({
                    ...item,
                    iban: item.iban!,
                    market,
                  })
                );
              break;
          }
          return accounts;
        }

        throw new Error('Market could not be determined');
      },
      merge: (currentCacheData, responseData) => {
        const existingAccountIds = new Set(
          currentCacheData.map((item) => item.iban ?? item.bban)
        );

        return responseData.map(
          (item): IOpenBankingAccount => ({
            ...item,
            isNewlyAdded: !existingAccountIds.has(item.iban ?? item.bban),
          })
        );
      },
    }),
    getIndicativeOffer: build.query<
      TLoanApplicationDTO['getIndicativeOfferRes'],
      GetIndicativeOfferQueryArg
    >({
      query: ({ applicationUuid }) => ({
        url: `/indicative-offer/${applicationUuid}`,
      }),
      // TODO: when we update to redux/rtk 2.7 or newer, add "responseSchema" here
      // and provide the loanApplicationGetIndicativeOfferResSchema
    }),
    getPaymentPlan: build.query<
      TPaymentPlanDTO['getPaymentPlanRes'],
      GetPaymentPlanQueryArg
    >({
      query: ({ applicationUuid, loanAmount, loanTerm }) => ({
        url: `/payment-plan/applicationUuid/${applicationUuid}`,
        params: {
          loanAmount,
          loanTerm,
        },
      }),
    }),
  }),
});

export const {
  useGetBankAccountsQuery,
  useGetIndicativeOfferQuery,
  useGetPaymentPlanQuery,
  util: onboardingApiUtil,
} = onboardingApi;
